/**
 * WlTenantMasterCustomerCard — vínculo PERMANENTE entre o tenant filho
 * e um cliente (crm_customer) do tenant master.
 *
 * - Estado "não vinculado": busca + selecionar + AlertDialog de confirmação.
 * - Estado "vinculado": exibe dados do cliente e badge "vínculo permanente"
 *   (sem botão para alterar/remover — integridade dos dados).
 *
 * RELEASE: 2026-06-14 v1.0.0
 */
import { useMemo, useState } from "react";
import { Loader2, Lock, Search, Link2, AlertTriangle } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { InlineNotice } from "@/components/ui/inline-notice";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useWlPermissions } from "@/contexts/WlPermissionsContext";
import {
  useWlMasterCustomerSearch,
  useWlSetTenantMasterCustomer,
  useWlTenantMasterCustomer,
  type WlMasterCustomerSearchResult,
} from "@/hooks/wl/useWlTenantMasterCustomer";

interface Props {
  tenantId: string;
}

export function WlTenantMasterCustomerCard({ tenantId }: Props) {
  const { canEdit } = useWlPermissions();
  const canEditTenants = canEdit("wl.tenants");
  const { toast } = useToast();

  const [search, setSearch] = useState("");
  const [pending, setPending] = useState<WlMasterCustomerSearchResult | null>(null);

  const { data: linked, isLoading: linkedLoading } =
    useWlTenantMasterCustomer(tenantId);

  const searchEnabled = !linked && canEditTenants;
  const { data: results = [], isFetching: searching } = useWlMasterCustomerSearch(
    tenantId,
    search,
    searchEnabled,
  );

  const setLink = useWlSetTenantMasterCustomer(tenantId);

  const handleConfirm = async () => {
    if (!pending) return;
    try {
      await setLink.mutateAsync(pending.id);
      toast({ title: "Cliente vinculado com sucesso." });
      setPending(null);
      setSearch("");
    } catch (err: any) {
      toast({
        title: "Falha ao vincular cliente",
        description: err?.message ?? String(err),
        variant: "destructive",
      });
    }
  };

  const headerIcon = useMemo(
    () => (linked ? <Lock className="h-4 w-4 text-primary" /> : <Link2 className="h-4 w-4 text-primary" />),
    [linked],
  );

  return (
    <>
      <Card className="rounded-xl border border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            {headerIcon}
            Cliente do tenant master
          </CardTitle>
          <CardDescription className="text-xs">
            Define qual <strong>cliente</strong> (no CRM do tenant master) representa este tenant
            para fins de faturamento. O vínculo é <strong>permanente</strong>.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-3">
          {linkedLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Carregando…
            </div>
          ) : linked ? (
            // ----------------- Estado: vinculado -----------------
            <div className="flex items-start justify-between gap-3 rounded-md border bg-muted/30 p-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">
                    {linked.display_name || "—"}
                  </span>
                  <Badge variant="secondary" className="text-[10px] gap-1">
                    <Lock className="h-3 w-3" />
                    Vínculo permanente
                  </Badge>
                </div>
                <div className="text-[11px] text-muted-foreground space-x-2">
                  {linked.doc && <span>Doc: {linked.doc}</span>}
                  {linked.customer_type && <span>· {linked.customer_type}</span>}
                  {linked.cidade && (
                    <span>
                      · {linked.cidade}
                      {linked.estado ? `/${linked.estado}` : ""}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground font-mono">
                  ID: {linked.crm_customer_id.slice(0, 8).toUpperCase()}
                </div>
              </div>
            </div>
          ) : (
            // ----------------- Estado: não vinculado -----------------
            <div className="space-y-2">
              <InlineNotice variant="warning">
                Vincule um cliente do tenant master para habilitar a seleção de plano e
                recorrências abaixo. Esta operação é irreversível.
              </InlineNotice>

              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nome, razão social, CPF ou CNPJ…"
                  className="pl-8 h-9 text-sm"
                  disabled={!canEditTenants}
                />
              </div>

              <div className="rounded-md border max-h-64 overflow-y-auto divide-y">
                {searching && (
                  <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Buscando…
                  </div>
                )}
                {!searching && results.length === 0 && (
                  <div className="p-3 text-xs text-muted-foreground">
                    {search.trim()
                      ? "Nenhum cliente encontrado."
                      : "Digite para buscar clientes do tenant master."}
                  </div>
                )}
                {!searching &&
                  results.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setPending(c)}
                      disabled={!canEditTenants}
                      className="w-full text-left p-2.5 hover:bg-muted/50 transition-colors flex items-center justify-between gap-2"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {c.display_name || "—"}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {c.doc ? `Doc: ${c.doc}` : "Sem documento"}
                          {c.cidade ? ` · ${c.cidade}${c.estado ? "/" + c.estado : ""}` : ""}
                        </div>
                      </div>
                      <Badge variant="outline" className="text-[10px] shrink-0">
                        Vincular
                      </Badge>
                    </button>
                  ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AlertDialog de confirmação (vínculo permanente) */}
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Vincular cliente do master — ação irreversível
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Você está prestes a vincular este tenant ao cliente{" "}
                  <strong>{pending?.display_name}</strong>
                  {pending?.doc ? ` (${pending.doc})` : ""}.
                </p>
                <p className="text-destructive">
                  Após confirmar, <strong>não será possível alterar nem remover</strong>{" "}
                  esse vínculo. Esta restrição garante a integridade do faturamento.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={setLink.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              disabled={setLink.isPending}
            >
              {setLink.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Vinculando…
                </>
              ) : (
                "Confirmar vínculo"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
